import { useMemo } from 'react'

import { B2BNavigationVisibilityPolicy } from '@react-native-common/common'

import config from '@app/config'
import type { UserState } from '@app/store/user'
import { isImpersonatedB2BSession } from '@app/utils/token/isImpersonatedB2BSession'

import type {
  ClientAccountVariables,
  NavigationVisibleContextResult,
  UserWithAvatar,
} from './types'

export const resolveB2BNavigationVisibleContext = (
  userState: UserState
): NavigationVisibleContextResult => {
  const currentUser = userState.currentUser as UserWithAvatar
  const hasCurrentUser = Boolean(currentUser.id)
  const isActAs = isImpersonatedB2BSession()

  const isDeveloper =
    currentUser.isDeveloper ?? userState.account.type === 'Developer'
  const isPartner =
    currentUser.isPartner ?? userState.account.type === 'Partner'
  const isFranchise =
    currentUser.isFranchise ?? userState.account.type === 'Franchise'
  const hasProjects = currentUser.hasProjects ?? false

  const accountId = Number(currentUser.accountId ?? userState.account.id)
  const clientsAccounts = (
    (config.variables as ClientAccountVariables)?.clientsAccounts ?? []
  ).map(Number)
  const isTestEnv = config.environment !== 'production'

  const roles = (currentUser.accountsRoles ?? [])
    .filter((accountRole) => accountRole.accountId === userState.account.id)
    .map((accountRole) => accountRole.role)
    .filter((role): role is string => typeof role === 'string')

  const permissions = (userState.permissions ?? [])
    .filter((permission) => {
      return permission.permission && permission.value === 'Allow'
    })
    .map((permission) => permission.permission)
    .filter((permission): permission is string => typeof permission === 'string')

  const resolvedContext = B2BNavigationVisibilityPolicy.resolveVisibleContext({
    hasCurrentUser,
    roles,
    permissions,
    accountType: userState.account.type,
    accountId,
    clientAccountIds: clientsAccounts,
    isDeveloper,
    isPartner,
    isFranchise,
    hasProjects,
    isAllowedToPromote: currentUser.isAllowedToPromote,
    isTestEnv,
  })

  return {
    ...resolvedContext,
    isActAs,
  }
}

export const useB2BNavigationVisibleContext = (
  userState: UserState
): NavigationVisibleContextResult => {
  return useMemo(
    () => resolveB2BNavigationVisibleContext(userState),
    [userState]
  )
}
